// Centralized helpers for building / parsing profile URLs.
// Target scheme:
//   /:mainSlug/:subSlug?/:subSubSlug?/:username
// Notes:
// - We use category `slug` values (stable, SEO-friendly)
// - We always encode path segments to avoid breaking URLs

function encodeSeg(v) {
  return encodeURIComponent(String(v || '').trim());
}

/**
 * Build canonical profile URL path.
 *
 * @param {Object} args
 * @param {Object} args.profile - profile object (expects { id, username })
 * @param {Object} args.categoryPath - { main, sub, subSub } (expects .slug)
 * @returns {string} Path starting with '/'
 */
export function buildProfilePath({ profile, categoryPath } = {}) {
  // IMPORTANT: the URL uses username_slug (latin transliteration), NOT the display username.
  // Fallback to username for legacy data.
  const username = profile?.username_slug || profile?.username;
  const mainSlug = categoryPath?.main?.slug;
  const subSlug = categoryPath?.sub?.slug;
  const subSubSlug = categoryPath?.subSub?.slug;

  // If we don't have a usable username or category, fall back to the legacy route.
  if (!username || !mainSlug) {
    const id = profile?.id;
    return id ? `/user/${encodeSeg(id)}` : '/';
  }

  const parts = [mainSlug, subSlug, subSubSlug, username].filter(Boolean).map(encodeSeg);
  return `/${parts.join('/')}`;
}

/**
 * Convenience: canonical absolute URL for SEO.
 */
export function buildProfileCanonicalUrl({ baseUrl, profile, categoryPath } = {}) {
  const path = buildProfilePath({ profile, categoryPath });
  return `${String(baseUrl).replace(/\/$/, '')}${path}`;
}
